import { useState } from 'react'

export type Validator = {
  trigger: () => Promise<boolean>
  formName: string
}

export type ValidatorMap = Map<string, Validator>
export type AddValidatorFunc = (id: string, validator: Validator) => void
export type RemoveValidatorFunc = (id: string) => void

/**
 * Manage a collection of validators and check all of them at once.
 *
 * @return {AddValidatorFunc} addValidator - Function to add a new validator to the collection.
 * @return {Validator} hasValidationErrors - Function to run all the registered validators.
 */
const useValidators = (): {
  addValidator: AddValidatorFunc
  removeValidator: RemoveValidatorFunc
  hasValidationErrors: () => Promise<Boolean>
} => {
  const [validators, setValidators] = useState<ValidatorMap>(new Map())

  return {
    addValidator: (id: string, validator: Validator) => {
      setValidators(oldValidators => {
        if (oldValidators.has(id)) {
          console.debug(`Overriding existing validator with id=${id}`)
        } else {
          console.debug(`Adding validator id=${id}:`, validator)
        }
        return oldValidators.set(id, validator)
      })
    },
    removeValidator: (id: string) => {
      setValidators(oldValidators => {
        const didRemove = oldValidators.delete(id)
        if (didRemove) {
          console.debug(`Removed validator id=${id}`)
        } else {
          console.warn(`Tried to remove validator id=${id} - but it was not there!`)
        }
        return oldValidators
      })
    },
    hasValidationErrors: async () => {
      validators.forEach((validator, id) => {
        validator.trigger().then((value) => {console.debug(`Validator id=${id}/formName=${validator.formName} -> ${value}`)})
      })
      return (await Promise.all(Array.from(validators.values()).map(val => val.trigger()))).some(
        r => r === false
      )
    },
  }
}

export default useValidators

export type AddPaymentValidatorFunc = (
  validator: Validator,
  index: number
) => void
export type RemovePaymentValidatorFunc = (index: number) => void

/**
 * Manage a collection of validators and check all the validators at once.
 *
 * @return {AddPaymentValidatorFunc} addPaymentValidator - Function to add a new validator to the collection.
 * @return {RemovePaymentValidatorFunc} removePaymentValidator - Function to remove a validator from the collection.
 * @return {Validator} hasValidationErrors - Function to run all the registered validators.
 */
export const usePaymentValidators = (
  size: number
): {
  addValidator: AddPaymentValidatorFunc
  removeValidator: RemovePaymentValidatorFunc
  hasValidationErrors: () => Promise<Boolean>
} => {
  const [validators, setValidators] = useState<Validator[]>(
    Array(size).fill({trigger: async () => true, formName: "payment"})
  )

  return {
    addValidator: (validator: Validator, index: number) => {
      setValidators(oldValidators => {
        oldValidators[index] = validator
        return oldValidators
      })
    },
    removeValidator: (index: number) => {
      setValidators(oldValidators => {
        oldValidators[index].trigger = async () => true
        return oldValidators
      })
    },
    hasValidationErrors: async () => {
      return (await Promise.all(Object.values(validators).map(val => val.trigger()))).some(
        r => r === false
      )
    }
  }
}
